import { memo, useState } from 'react'
import IconButton from '@mui/material/IconButton'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import { Observable } from 'rxjs'
import { FormattedMessage } from 'react-intl'
import Tooltip, { TooltipProps } from '@mui/material/Tooltip'
import numberToHuman from 'utils/format/numberToHuman'

import styles from './FavButton.pcss'

interface FavButtonProps {
  isFavorite: boolean
  hover?: boolean
  // EXPL: Expand on hover of the surrounding card instead of a JS-tracked flag, so hovering a card
  // does not have to re-render it. Relies on the `data-card` attribute the card puts on its root.
  expandOnCardHover?: boolean
  count?: number
  className?: string
  outline?: boolean
  outlineColor?: string
  iconClassName?: string
  size?: 'small' | 'medium' | 'large'
  tooltipPlacement?: TooltipProps['placement']
  onFavorite: () => Observable<any>
  onUnfavorite: () => Observable<any>
}

export function FavButton(props: FavButtonProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const prettyCount = props.count ? numberToHuman(props.count) : 0

  const toggleFavorite = (e: any) => {
    e.stopPropagation()
    setTooltipOpen(false)
    if (saving) {
      return
    }

    setSaving(true)
    const callback = props.isFavorite ? props.onUnfavorite : props.onFavorite
    callback().subscribe(() => {
      setSaving(false)
    })
  }

  const showTooltip = () => {
    setTooltipOpen(true)
  }

  const hideTooltip = () => {
    setTooltipOpen(false)
  }

  const outlineClassName = props.outline ? styles.outline : ''
  const cardHoverClassName = props.expandOnCardHover ? styles['expand-on-card-hover'] : ''
  const style = props.outlineColor ? { color: props.outlineColor } : {}

  return (
    <Tooltip
      title={<FormattedMessage id="content.tooltip.btn-favorite" />}
      placement={props.tooltipPlacement || 'left'}
      open={tooltipOpen}
    >
      <IconButton
        data-testid="fav-button"
        onClick={toggleFavorite}
        className={`${styles.btn} ${props.hover ? styles.expanded : ''} ${cardHoverClassName} ${props.className || ''} ${outlineClassName}`}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        size={props.size || 'large'}
      >
        {
          props.isFavorite
            ? <FavoriteIcon className={`${styles.icon} ${props.iconClassName || ''}`} />
            : (
              <FavoriteBorderIcon
                className={`${styles.icon} ${styles['icon-outline']} ${props.iconClassName || ''}`}
                style={style}
              />
            )
        }
        {prettyCount !== 0 && <span className={styles.count}>{prettyCount}</span>}
      </IconButton>
    </Tooltip>
  )
}

export default memo(FavButton)
